import React, { useState } from 'react';
import { Box, Typography, Button, Container, useTheme } from '@mui/material';
import { Link } from "react-router-dom";

const sections = [
    {
        bgColor: 'rgba(255, 255, 255, 0.7)',
        clip: 'polygon(0 0, 100% 5%, 100% 100%, 0 95%)',

        description: 'Intrinsic Informer gives you the capability to ensure compliant and confident execution of the protocol by sites, strengthen relationships with investigators, and efficiently plan clinical studies - all within one intuitive platform. Accurately identify sites with an appropriate patient population based on study criteria. Oversee studies with clear dashboards to identify and quickly resolve engagement and compliance issues.',
        showButton: false,
    },
    {
        image: '/insightsgrid.png',
        bgColor: 'rgba(99, 157, 235, 0.16)',
        clip: 'polygon(0 5%, 100% 0, 100% 95%, 0 100%)',
        title: 'FEATURES & BENEFITS',
        paragraphs: [
            'Save Time with Imported Mailing Lists – easily import and build a mailing list of investigators for distribution of surveys for clinical trial feasibility at the trial, country and site levels.',
            'Question Library – question library allows for standardized feasibility surveys across studies and programs.',
            'User-Friendly Feasibility – Informer clinical trial feasibility tool allows site personnel to begin answering feasibility questions, save, and complete work later.',
            'Automate Investigator Relationship Management – simplify investigator relationship management by automatically tracking which sites haven’t opened or completed their site feasibility surveys all to enhance Clinical Trial Management.',
            'Create Alerts & Tasks – automate investigator relationship management by setting alerts and assigning tasks.',
            'Feasibility Data Aggregated All One Place – trial, country, and site feasibility data are aggregated in one place, allowing you to select the best investigators or analyze responses across all investigators, regardless of study or program.',
            'Feasibility Integrated with Clinical Trial Management – easily move to the next step in the study startup process in the clinical trial management process – feasibility data can be pushed into Intrinsic CTMS® to manage investigators as sites are brought online.',
        ],
    },
    {
        bgColor: 'rgba(255, 255, 255, 0.7)',
        clip: 'polygon(0 0, 100% 5%, 100% 100%, 0 95%)',
        title: 'READY TO SEE MORE?',
        description: 'We’ll walk you through all the features and functionality that will help you use Informer to its fullest potential.',
        showButton: true,
    },
];

const IntrinsicInformer = () => {

    const [showVideo, setShowVideo] = useState(false);
    const theme = useTheme();


    return (
        <>
            <Box sx={{ py: 2, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography

                    sx={{
                        fontWeight: 'bold',
                        color: theme.palette.mode === "dark" ? "white" : "black",
                        fontSize: '1.5rem',
                        mb: 2,
                    }}
                >
                    INTRINSIC INFORMER
                </Typography>

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-end' },
                        maxWidth: '100%',
                    }}
                >
                    {!showVideo ? (
                        <img
                            src="/informarVideoimage.png" // thumbnail image
                            alt="Click to play"
                            onClick={() => setShowVideo(true)}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '12px',
                                maxWidth: '500px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                width: '100%',
                                height: '300px',
                            }}
                        />
                    ) : (
                        <iframe
                            width="500"
                            height="300"
                            style={{
                                borderRadius: '12px',
                                maxWidth: '500px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                            }}
                            src="https://www.youtube.com/embed/n8NSa-y97CE?autoplay=1" // ✅ YouTube embed link
                            title="Informer Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    )}

                </Box>
            </Box>

            {sections.map((section, index) => (
                <Box
                    key={index}
                    sx={{
                        backgroundColor: section.bgColor,

                        clipPath: {
                            xs: 'none',
                            sm: 'none',
                            md:
                                index % 2 === 0
                                    ? 'polygon(0 0, 100% 20%, 100% 100%, 0 80%)'
                                    : 'polygon(0 20%, 100% 0, 100% 80%, 0 100%)',
                        },
                        position: 'relative',
                        zIndex: 10 - index,
                        mt: index === 0 ? 0 : '-80px',
                        py: { xs: 16, md: 12 },
                        px: { xs: 2, md: 6 },
                    }}
                >
                    <Container>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: {
                                    xs: 'column',
                                    md: index % 2 === 0 ? 'row' : 'row-reverse',
                                },
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'left',
                                    px: { xs: 2, md: 8 },
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.mode === "dark" ? "white" : "black",
                                        mb: 4,
                                        fontSize: '1.5rem',
                                    }}
                                >
                                    {section.title}
                                </Typography>
                                {section.showButton && (
                                    <Button
                                        variant="contained"
                                        component={Link}
                                        to="/request-demo"
                                        sx={{
                                            backgroundColor: '#004aad',
                                            color: '#FFFFFF',
                                            px: '40px',
                                            py: '6px',
                                            fontSize: '1.4rem',
                                            borderRadius: '50px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            boxShadow: '0px 4px 12px rgba(0, 174, 239, 0.4)',
                                            transition: 'transform 0.3s ease, background-color 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#004aad',
                                                transform: 'scale(1.05)',
                                            },
                                            mb: 4,
                                        }}
                                    >
                                        Request a Demo
                                    </Button>
                                )}

                                {section.paragraphs
                                    ? section.paragraphs.map((para, idx) => {
                                        const match = para.split("–");
                                        const boldPart = match[0] || "";
                                        const rest = match.slice(1).join("–");

                                        return (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    width: "100%",
                                                    display: "block",
                                                    textAlign: "left",
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontSize: "1rem",
                                                        fontFamily: "Montserrat, sans-serif",
                                                        color: theme.palette.mode === "dark" ? "white" : "black",
                                                    }}
                                                >
                                                    <strong>{boldPart.trim()}</strong>
                                                    {rest ? " – " + rest.trim() : ""}
                                                </Typography>
                                            </Box>
                                        );
                                    })
                                    : (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: section.showButton ? 4 : 0,
                                                textAlign: "left",
                                                px: 4,
                                                fontSize: "1rem",
                                                fontFamily: "Montserrat, sans-serif",
                                                color: theme.palette.mode === "dark" ? "white" : "black",
                                            }}
                                        >
                                            {section.description}
                                        </Typography>
                                    )}


                            </Box>

                        </Box>
                    </Container>
                </Box>
            ))}
        </>
    );
};

export default IntrinsicInformer;


